import { DateTime } from 'luxon';
import { Instance, applySnapshot, flow, types } from 'mobx-state-tree';

import {
	changeIsPresent,
	getCommentQuestions,
	getGradeBook,
	regeneratedIAFeedback,
	resetProgress,
	saveGradeBook,
} from '@common/api/gradebooks';
import {
	deleteGrades,
	postGrades,
	postIndivGrades,
	saveGrades,
	saveIndivGrades,
} from '@common/api/grades';
import RubricLevel from '@common/models/Rubrics/RubricLevel';

import { IChunkModel } from '../Chunks/Chunk';
import ScheduleLesson from '../Schedules/ScheduleLesson';
import Grade from './Grade';
import { SemesterModel } from './Semester';

type TCommentQuestion = {
	label: string;
	subquestions: string;
};

type TVolatile = {
	loading: boolean;
	commentQuestions: TCommentQuestion[];
};

const GradeBookEvent = types
	.model('GradeBookEvent', {
		id: types.identifierNumber,
		gradebook_id: types.maybeNull(types.number),
		type: types.maybeNull(types.string),
		date: types.maybeNull(types.string),
	})
	.views((self) => ({
		get dateTime() {
			return DateTime.fromSQL(self.date);
		},
	}))
	.views((self) => ({
		get dateString() {
			return self.dateTime.toISODate();
		},
		get timeString() {
			return self.dateTime.toLocaleString(DateTime.TIME_24_SIMPLE);
		},
	}));

const GradeBook = types
	.model('GradeBook', {
		id: types.identifierNumber,
		manual_grade: types.maybeNull(types.string),
		topic_schedule_id: types.maybeNull(types.number),
		student_id: types.maybeNull(types.number),
		gb_date: types.maybeNull(types.string),
		general_gradebook_id: types.maybeNull(types.number),
		is_done: types.number,
		is_present: types.maybeNull(types.number),
		schedule_lesson: types.maybeNull(ScheduleLesson),
		grades: types.optional(types.array(Grade), []),
		final_grade: types.maybeNull(types.frozen()),
		convert_final_grade: types.maybeNull(types.string),
		final_comment: types.maybeNull(types.string),
		semester: types.maybeNull(SemesterModel),
		events: types.optional(types.array(GradeBookEvent), []),
		updated_at: types.maybeNull(types.string),
		is_show_final_comment: types.optional(types.boolean, true),
		location_id: types.maybeNull(types.number),
		stage_result: types.maybeNull(types.number),
		comment_answers: types.maybe(types.maybeNull(types.optional(types.array(types.string), []))),
		notes: types.maybeNull(types.string),
		is_block_edit_grade: types.optional(types.boolean, false),
		is_free: types.optional(types.boolean, false),
		is_failed: types.optional(types.boolean, false),
		is_credited: types.optional(types.boolean, false),
		academic_year_id: types.maybeNull(types.number),
		academic_semester_id: types.maybeNull(types.number),
		schedule_lesson_id: types.maybeNull(types.number),
		indiv_schedule_lesson_id: types.maybeNull(types.number),
		talented_pdf: types.maybeNull(types.frozen()),
		is_self_paced: types.optional(types.boolean, false),
		is_processed: types.maybeNull(types.boolean),
		feedback_in_response: types.maybeNull(types.string),
		rubricLevels: types.optional(types.array(RubricLevel), []),
	})
	.volatile(
		(): TVolatile => ({
			loading: false,
			commentQuestions: [],
		}),
	)
	.views((self) => ({
		get dateTime() {
			return DateTime.fromSQL(self.gb_date);
		},
		getStudentFilesByChunkId(chunk: IChunkModel) {
			if (chunk.chunk_type?.name !== 'Student') {
				return null;
			}

			const files = self.grades
				.filter((grade) => grade?.chunk?.id === chunk.id)
				.map((grade) => ({
					gradeId: grade.id,
					student_url: grade.student_url,
					file: grade.student_file,
					reject_comment: grade.reject_comment || null,
					rejected_at: grade.rejected_at || null,
				}));

			return files;
		},
	}))
	.actions((self) => ({
		fetch: flow(function* (query: any = {}) {
			try {
				self.loading = true;

				const result = yield getGradeBook(self.id, query);

				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		fetchQuestions: flow(function* () {
			try {
				self.loading = true;

				const result = yield getCommentQuestions({});

				self.commentQuestions = result;
			} finally {
				self.loading = false;
			}
		}),
	}))
	.actions((self) => ({
		save: flow(function* (data: any) {
			self.loading = true;
			try {
				const result = yield saveGradeBook(self.id, data);
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		regenerateAIFeedback: flow(function* ({ comment }) {
			self.loading = true;
			try {
				const result = yield regeneratedIAFeedback({ id: self.id, comment });
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		changeIsPresent: flow(function* (index: number) {
			self.loading = true;
			try {
				const result = yield changeIsPresent([
					{
						id: self.id,
						is_present: index,
					},
				]);
				const gradeBook = result.find((gradeBook: any) => gradeBook.id === self.id);
				applySnapshot(self, gradeBook);
			} finally {
				self.loading = false;
			}
		}),
		resetGrades: flow(function* () {
			self.loading = true;
			try {
				yield resetProgress(self.id);
				yield self.fetch();
			} finally {
				self.loading = false;
			}
		}),
		saveGrades: flow(function* (
			data: any,
			teacher_id: number,
			isIndiv = false,
			service_id?: number,
			comment_answers?: any,
			homeworkOnly = false,
			rubricLevels?: number[],
		) {
			self.loading = true;
			try {
				const gradesToPost = data
					.filter((grade: any) => !grade?.id)
					.map((grade: any) => ({
						gradebook_id: self.id,
						gradeType: grade.gradeType,
						grade_type_id: grade.gradeType.id,
						comment: grade?.comment,
						teacher_id,
						grade: `${grade.grade}`,
						manual_grade: `${grade.grade}`,
						annotate_url: Object.values(
							self.grades?.find((g) => g.id === grade?.id)?.annotatedFiles || {},
						).map((file) => file?.url),
						annotate_file: Object.values(
							self.grades?.find((g) => g.id === grade?.id)?.annotatedFiles || {},
						),
					}));
				if (gradesToPost.length) {
					yield isIndiv ? postIndivGrades(gradesToPost) : postGrades(gradesToPost);
				}

				const gradesToSave = data
					.filter((grade: any) => !!grade?.id)
					.map((grade: any) => ({
						...grade,
						grade: `${grade.grade}`,
						grade_type_id: grade?.gradeType?.id,
						manual_grade: `${grade.grade}`,
						teacher_id,
						annotate_url: Object.values(
							self.grades?.find((g) => g.id === grade?.id)?.annotatedFiles || {},
						).map((file) => file?.url),
						annotate_file: Object.values(
							self.grades?.find((g) => g.id === grade?.id)?.annotatedFiles || {},
						),
					}));
				if (gradesToSave.length) {
					yield isIndiv
						? saveIndivGrades(gradesToSave)
						: saveGrades({
								grades: gradesToSave,
								service_id,
								comment_answers: comment_answers
									? comment_answers.map((answer: any) => answer || '')
									: undefined,
								rubricLevels,
							});
				}

				yield self.fetch(homeworkOnly ? { lesson_work_type: 'homework' } : {});
			} finally {
				self.loading = false;
			}
		}),
		remove: flow(function* (ids: number[]) {
			self.loading = true;
			try {
				yield deleteGrades(
					ids.map((id) => ({
						id,
					})),
				);
				applySnapshot(self.grades, [
					...self.grades.filter((grade) => !ids.find((id) => grade.id === id)),
				]);
			} finally {
				self.loading = false;
			}
		}),
	}));

export default GradeBook;

export interface IGradeBookModel extends Instance<typeof GradeBook> {}
